import type {
  CommonRequestFn,
  CommonRequestFnWithoutPayload
} from "@marketplace/http";
import type { BaseResponse } from "@marketplace/shared/types";

import { ENDPOINTS } from "@/constants/endpoints";
import { httpClient } from "@/http";

import type { AssignPasswordRequestDto } from "../dtos/assign-password-request-dto";
import type { AssignPasswordResponseDto } from "../dtos/assign-password-response-dto";
import type { ChangePasswordRequestDto } from "../dtos/change-password-request-dto";
import type { ForgotPasswordRequestDto } from "../dtos/forgot-password-request-dto";
import type { ForgotPasswordResponseDto } from "../dtos/forgot-password-response-dto";
import type { LoginEmailRequestDto } from "../dtos/login-email-request-dto";
import type { LoginEmailResponseDto } from "../dtos/login-email-response-dto";
import type { RefreshTokenResponseDto } from "../dtos/refresh-token-response-dto";
import type { RegisterEmailRequestDto } from "../dtos/register-email-request-dto";
import type { RegisterEmailResponseDto } from "../dtos/register-email-response-dto";
import type { VerifyOtpRequestDto } from "../dtos/verify-otp-request-dto";

type AuthServiceContract = {
  registerEmail: CommonRequestFn<
    RegisterEmailRequestDto,
    RegisterEmailResponseDto
  >;
  loginEmail: CommonRequestFn<LoginEmailRequestDto, LoginEmailResponseDto>;
  verifyOtp: CommonRequestFn<
    VerifyOtpRequestDto & {
      page: "register" | "login";
    },
    LoginEmailResponseDto
  >;
  sendOtp: CommonRequestFn<
    Omit<VerifyOtpRequestDto, "otp_code">,
    BaseResponse<null>
  >;
  refreshToken: CommonRequestFnWithoutPayload<RefreshTokenResponseDto>;
  logout: CommonRequestFnWithoutPayload<BaseResponse<string>>;
  forgotPassword: CommonRequestFn<
    ForgotPasswordRequestDto,
    ForgotPasswordResponseDto
  >;
  changePassword: CommonRequestFn<
    ChangePasswordRequestDto,
    ForgotPasswordResponseDto
  >;
  assignPassword: CommonRequestFn<
    AssignPasswordRequestDto,
    AssignPasswordResponseDto
  >;
};

export const AuthService: AuthServiceContract = {
  registerEmail: async ({ payload, options }) =>
    await httpClient
      .post(ENDPOINTS.AUTH.REGISTER_EMAIL, {
        ...options,
        json: payload
      })
      .json(),

  loginEmail: async ({ payload, options }) =>
    await httpClient
      .post(ENDPOINTS.AUTH.LOGIN_EMAIL, {
        ...options,
        json: payload
      })
      .json<LoginEmailResponseDto>(),

  verifyOtp: async ({ payload, options }) => {
    let url = "";

    switch (payload.page) {
      case "register":
        url = ENDPOINTS.AUTH.REGISTER_EMAIL_VERIFY;
        break;
      case "login":
        url = ENDPOINTS.AUTH.LOGIN_EMAIL_VERIFY;
    }

    return httpClient
      .post(url, {
        ...options,
        json: {
          email: payload.email,
          otp_code: payload.otp_code
        }
      })
      .json<LoginEmailResponseDto>();
  },

  sendOtp: async ({ payload, options }) =>
    await httpClient
      .post(ENDPOINTS.AUTH.SEND_OTP, {
        ...options,
        json: {
          email: payload.email
        }
      })
      .json<BaseResponse<null>>(),

  refreshToken: async (params) =>
    await httpClient
      .post(ENDPOINTS.AUTH.REFRESH_TOKEN, {
        ...params?.options
      })
      .json<LoginEmailResponseDto>(),

  forgotPassword: async ({ payload, options }) => {
    return await httpClient
      .post(ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        ...options,
        json: payload
      })
      .json<ForgotPasswordResponseDto>();
  },
  changePassword: async ({ payload, options }) => {
    return await httpClient
      .post(ENDPOINTS.AUTH.RESET_PASSWORD, {
        ...options,
        json: {
          password: payload.password
        },
        searchParams: {
          token: payload.resetToken
        }
      })
      .json();
  },

  logout: async () =>
    await httpClient.post(ENDPOINTS.AUTH.LOGOUT).json<BaseResponse<string>>(),

  assignPassword: async ({ payload, options }) =>
    await httpClient
      .put(ENDPOINTS.USERS.SELLER.ASSIGN_PASSWORD, {
        ...options,
        json: {
          password: payload.password,
          confirm_password: payload.confirm_password
        },
        searchParams: {
          token: payload.token
        }
      })
      .json<AssignPasswordResponseDto>()
};
